import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider, useApp } from "@/contexts/AppContext";
import { ChatThemeProvider } from "@/contexts/ThemeContext";
import BannedScreen from "@/components/BannedScreen";
import WarningToast from "@/components/WarningToast";

// Keep landing route eager so initial paint isn't delayed
import Index from "./pages/Index";

// Lazy route loaders — kept as named imports so we can both lazy-mount AND
// proactively prefetch them after the app is interactive. Prefetching means
// switching pages becomes effectively instant (no chunk-loading spinner).
const loadMatchmaking = () => import("./pages/MatchmakingPage");
const loadCall = () => import("./pages/CallPage");
const loadSettings = () => import("./pages/SettingsPage");
const loadProfile = () => import("./pages/ProfilePage");
const loadCoins = () => import("./pages/CoinsPage");
const loadShop = () => import("./pages/ShopPage");
const loadThemeShop = () => import("./pages/ThemeShopPage");
const loadAppearance = () => import("./pages/AppearancePage");
const loadMessages = () => import("./pages/MessagesPage");
const loadRandomChat = () => import("./pages/RandomChatPage");
const loadSearchUsers = () => import("./pages/SearchUsersPage");
const loadNotFound = () => import("./pages/NotFound");
const loadResetPassword = () => import("./pages/ResetPasswordPage");
const loadTerms = () => import("./pages/TermsPage");
const loadSupport = () => import("./pages/SupportPage");
const loadBugReport = () => import("./pages/BugReportPage");
const loadStaff = () => import("./pages/StaffDashboard");
const loadUpgrade = () => import("./pages/UpgradeAccountPage");
const loadReferralLanding = () => import("./pages/ReferralLandingPage");
const loadUnsubscribe = () => import("./pages/UnsubscribePage");
const loadLogin = () => import("./pages/LoginPage");

const MatchmakingPage = lazy(loadMatchmaking);
const CallPage = lazy(loadCall);
const SettingsPage = lazy(loadSettings);
const ProfilePage = lazy(loadProfile);
const CoinsPage = lazy(loadCoins);
const ShopPage = lazy(loadShop);
const ThemeShopPage = lazy(loadThemeShop);
const AppearancePage = lazy(loadAppearance);
const MessagesPage = lazy(loadMessages);
const RandomChatPage = lazy(loadRandomChat);
const SearchUsersPage = lazy(loadSearchUsers);
const NotFound = lazy(loadNotFound);
const ResetPasswordPage = lazy(loadResetPassword);
const TermsPage = lazy(loadTerms);
const SupportPage = lazy(loadSupport);
const BugReportPage = lazy(loadBugReport);
const StaffDashboard = lazy(loadStaff);
const UpgradeAccountPage = lazy(loadUpgrade);
const ReferralLandingPage = lazy(loadReferralLanding);
const UnsubscribePage = lazy(loadUnsubscribe);
const LoginPage = lazy(loadLogin);

// Prefetch every route chunk shortly after first paint so subsequent
// navigations don't show a loading screen.
// Prefetch lightweight route chunks only. Heavy chunks (CallPage with Agora
// SDK ~429 KB, StaffDashboard) are intentionally excluded — they load on
// demand to keep Max Potential FID low on initial page load.
const prefetchRoutes = () => {
  const loaders = [
    loadMessages, loadProfile, loadCoins, loadShop, loadSettings,
    loadMatchmaking, loadRandomChat, loadSearchUsers, loadThemeShop, loadAppearance,
    loadTerms, loadSupport, loadBugReport, loadResetPassword, loadNotFound,
  ];
  // Stagger to avoid creating one giant long task.
  loaders.forEach((l, i) => {
    setTimeout(() => { l().catch(() => {}); }, i * 50);
  });
};

// Prefetch the heavy CallPage chunk only when the user is about to need it
// (e.g. hovering matchmaking/random-chat). Exposed for components to call.
let callPrefetched = false;
export const prefetchCallChunk = () => {
  if (callPrefetched) return;
  callPrefetched = true;
  loadCall().catch(() => { callPrefetched = false; });
  loadRandomChat().catch(() => {});
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Fallback while a lazy chunk loads. Renders the same dark background so
// there is no jarring black flash when transitioning between routes (the
// browser briefly paints transparent → body bg before the chunk mounts).
const RouteFallback = () => (
  <div className="h-[100dvh] w-full bg-background" />
);

const AnimatedRoutes = () => {
  const { banInfo, logout, loading } = useApp();

  // Kick off route chunk prefetching once the app finishes booting.
  useEffect(() => {
    if (loading) return;
    const idle = (cb: () => void) => {
      if (typeof (window as any).requestIdleCallback === "function") {
        (window as any).requestIdleCallback(cb, { timeout: 2000 });
      } else {
        setTimeout(cb, 300);
      }
    };
    idle(prefetchRoutes);
  }, [loading]);

  if (banInfo) {
    return (
      <BannedScreen
        userId={banInfo.userId}
        banReason={banInfo.reason}
        bannedUntil={banInfo.bannedUntil}
        onSignOut={logout}
      />
    );
  }
  return (
    <>
      <WarningToast />
      <Suspense fallback={<RouteFallback />}>
        <main>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/matchmaking" element={<RandomChatPage />} />
          <Route path="/call" element={<RandomChatPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/coins" element={<CoinsPage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/themes" element={<ThemeShopPage />} />
          <Route path="/appearance" element={<AppearancePage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/random-chat" element={<RandomChatPage />} />
          <Route path="/search" element={<SearchUsersPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/report-bug" element={<BugReportPage />} />
          <Route path="/staff" element={<StaffDashboard />} />
          <Route path="/upgrade" element={<UpgradeAccountPage />} />
          <Route path="/ref/:code" element={<ReferralLandingPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </main>
      </Suspense>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AppProvider>
        <ChatThemeProvider>
          <BrowserRouter>
            <AnimatedRoutes />
          </BrowserRouter>
        </ChatThemeProvider>
      </AppProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
